import { ExpandedColumn } from "../types/columns";
import { clearHiddenFilters } from "./columnOptionsState";

export const hydrateColumn = ({
  column,
  defaultColumns,
}: {
  column: ExpandedColumn;
  defaultColumns: ExpandedColumn[];
}) => {
  const defaultCol = defaultColumns.find((dc) => dc.key === column.key);
  // Deep clone to prevent shared references from being mutated
  const mappedCol = {
    ...column,
    filter: column.filter ? { ...column.filter } : column.filter,
  };

  // Deep clone filter.value array to prevent mutation
  if (
    mappedCol.filter &&
    "value" in mappedCol.filter &&
    Array.isArray((mappedCol.filter as any).value)
  ) {
    (mappedCol.filter as any).value = [
      ...(mappedCol.filter as any).value,
    ];
  }

  // Ensure defaultCol exists before accessing its properties
  if (defaultCol) {
    // Handle date filters
    if (mappedCol.filter?.type === "date") {
      if (mappedCol.filter.date1) {
        mappedCol.filter.date1 = new Date(mappedCol.filter.date1);
      }
      if (mappedCol.filter.date2) {
        mappedCol.filter.date2 = new Date(mappedCol.filter.date2);
      }
    }

    // Restore non-persistable taxonomy filter configuration (exclude list,
    // label renderers, select mode, ...) from the default column definition.
    // Saved views only carry the serialized filter, so an `exclude` added later
    // (e.g. hiding OfferStatus.INVALIDATED) would otherwise never apply to them.
    if (
      mappedCol.filter?.type === "taxonomy" &&
      defaultCol.filter?.type === "taxonomy"
    ) {
      mappedCol.filter = {
        ...defaultCol.filter,
        ...mappedCol.filter,
        exclude: defaultCol.filter.exclude,
      };
    }

    // Safely assign detailsListColumn if it exists in defaultCol
    if (defaultCol.detailsListColumn) {
      mappedCol.detailsListColumn = defaultCol.detailsListColumn;
    }
  }

  return mappedCol;
};

/**
 * Hydrates the columns of a saved view. Columns that no longer exist in the default column definitions
 * (e.g. a removed Azure Search field) are dropped, so a stale sort/filter can't break the list query.
 * Default columns added after the view was saved are appended hidden, so every field stays selectable,
 * and filters stored on hidden columns are cleared, because hidden columns never filter.
 */
export const hydrateColumns = ({
  columns,
  defaultColumns,
}: {
  columns: ExpandedColumn[];
  defaultColumns: ExpandedColumn[];
}): ExpandedColumn[] => {
  const hydrated = columns
    .filter((column) => defaultColumns.some((dc) => dc.key === column.key))
    .map((column) => hydrateColumn({ column, defaultColumns }));
  const missing = defaultColumns.filter(
    (dc) => !hydrated.some((column) => column.key === dc.key)
  );
  // Fixed columns must stay a continuous prefix of the list.
  const missingFixed = missing.filter((dc) => dc.isFixed);
  const missingHidden = missing
    .filter((dc) => !dc.isFixed)
    .map((dc) => ({ ...dc, isVisible: false }));
  return clearHiddenFilters([...missingFixed, ...hydrated, ...missingHidden]);
};
